import { BufferAttribute, BufferGeometry, Sphere, Vector3 } from 'three';

/**
 * Kruhová síť pro hladinu moře, hustá u středu a řídnoucí k obzoru.
 *
 * Gerstnerovy vlny potřebují skutečné vrcholy — plochá deska ze dvou
 * trojúhelníků nemá co posouvat. Rovnoměrná mřížka přes 16 km by ale musela
 * mít desítky milionů vrcholů, aby byla u kamery dost jemná.
 *
 * Řešení: disk, kde poloměr prstenců roste geometricky. U kamery jsou
 * vrcholy po desítkách centimetrů, na obzoru po stovkách metrů — přesně
 * tam, kde to oko potřebuje. Síť jede s kamerou, vlny se počítají ze
 * světových souřadnic, takže hladina zůstává ukotvená ve světě.
 */
export function buildOceanDisc(rings: number, segments: number, innerRadius: number, outerRadius: number): BufferGeometry {
  const vertexCount = 1 + rings * segments;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const indices: number[] = [];

  normals[1] = 1;

  const growth = Math.pow(outerRadius / innerRadius, 1 / (rings - 1));

  for (let r = 0; r < rings; r++) {
    const radius = innerRadius * Math.pow(growth, r);
    for (let s = 0; s < segments; s++) {
      const angle = (s / segments) * Math.PI * 2;
      const i = 1 + r * segments + s;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = Math.sin(angle) * radius;
      normals[i * 3 + 1] = 1;
    }
  }

  // Vějíř ze středu do prvního prstence.
  for (let s = 0; s < segments; s++) {
    const a = 1 + s;
    const b = 1 + ((s + 1) % segments);
    indices.push(0, b, a);
  }

  for (let r = 0; r < rings - 1; r++) {
    const base = 1 + r * segments;
    const next = base + segments;
    for (let s = 0; s < segments; s++) {
      const s1 = (s + 1) % segments;
      // Pořadí je odvozené z vektorového součinu tak, aby normála mířila
      // nahoru. Opačné vinutí znamená, že backface culling zahodí celý
      // disk a hladina zmizí, aniž by cokoliv nahlásilo chybu.
      indices.push(base + s, base + s1, next + s);
      indices.push(base + s1, next + s1, next + s);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setIndex(indices);
  // Culling řešíme ručně (mesh je vždy pod kamerou), ale bounding sphere
  // musí existovat, aby three nepočítalo NaN.
  geometry.boundingSphere = new Sphere(new Vector3(), outerRadius);
  return geometry;
}
